import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const SEGMENTS = [
  { value: -300, label: '-300', color: '#f27059' },
  { value: -100, label: '-100', color: '#f4d35e' },
  { value: -100, label: '-100', color: '#f4d35e' },
  { value: 0, label: '0', color: '#b8b8ff' },
  { value: 0, label: '0', color: '#b8b8ff' },
  { value: 100, label: '100', color: '#57cc99' },
  { value: 100, label: '100', color: '#57cc99' },
  { value: 200, label: '200', color: '#38a3a5' },
  { value: 200, label: '200', color: '#38a3a5' },
  { value: 300, label: '300', color: '#22577a' },
  { value: 300, label: '300', color: '#22577a' },
  { value: 500, label: '500', color: '#ffd166' },
];

const COMMENTARY = {
  '-300': "That one stung.",
  '-100': "Not your favorite number.",
  '0': "Push — bragging rights deferred.",
  '100': "Heat rising.",
  '200': "Momentum shift.",
  '300': "Crowd’s feeling it.",
  '500': "Massive swing.",
};

const NEGATIVE_COMMENTARY = {
  '-300': "The physics look… suspect.",
  '-100': "Check the wheel — suspect physics?",
};

const POSITIVE_COMMENTARY = {
  '100': "Solid pull — smooth spin.",
  '200': "Clean hit! That one felt good.",
  '300': "W2J must’ve had a hand in that one.",
  '500': "Absolute heater — W2J can’t believe it either.",
};

const SEGMENT_ANGLE = 360 / SEGMENTS.length;
const SPIN_DURATION = 5000;
const EXTRA_ROTATIONS = 6;

const getMessageForValue = (value) => {
  const base = COMMENTARY[value.toString()];
  if (value < 0) {
    return `${base} ${NEGATIVE_COMMENTARY[value.toString()]}`;
  }
  if (value > 0) {
    return `${base} ${POSITIVE_COMMENTARY[value.toString()]}`;
  }
  return base;
};

const buildGradientStops = (segments) => {
  const stops = [];
  segments.forEach((segment, index) => {
    const start = index * SEGMENT_ANGLE;
    const end = start + SEGMENT_ANGLE;
    stops.push(`${segment.color} ${start}deg ${end}deg`);
  });
  return stops.join(', ');
};

const getBaseRotationForIndex = (index) => 90 - (index + 0.5) * SEGMENT_ANGLE;

const chooseRandomIndex = (length) => Math.floor(Math.random() * length);

// Build a circular ordering so that no two equal values are adjacent (including wrap-around)
const arrangeSegmentsNoAdjacency = (arr, maxRetries = 200) => {
  const n = arr.length;
  const key = (s) => s.value; // group key

  // Pre-group by value
  const groups = arr.reduce((m, s) => {
    (m[key(s)] ||= []).push(s);
    return m;
  }, {});

  const values = Object.keys(groups);

  const tryBuild = () => {
    // Shuffle items within each group and shuffle group order to add randomness
    values.forEach((v) => {
      const g = groups[v];
      for (let i = g.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [g[i], g[j]] = [g[j], g[i]];
      }
    });
    const shuffledValues = values.slice().sort(() => Math.random() - 0.5);

    const remaining = Object.fromEntries(shuffledValues.map((v) => [v, groups[v].slice()]));
    const order = [];

    while (order.length < n) {
      // Prefer values with highest remaining count, random tie-break
      const candidates = Object.entries(remaining)
        .filter(([, list]) => list.length > 0)
        .sort((a, b) => b[1].length - a[1].length || (Math.random() - 0.5));

      let placed = false;
      for (const [v, list] of candidates) {
        const last = order[order.length - 1];
        const first = order[0];
        if (last && key(last) === v) continue; // avoid adjacent equals
        if (order.length === n - 1 && first && key(first) === v) continue; // avoid wrap-around equal
        order.push(list.pop());
        placed = true;
        break;
      }
      if (!placed) return null; // dead-end, retry
    }
    return order;
  };

  for (let r = 0; r < maxRetries; r++) {
    const built = tryBuild();
    if (built) return built;
  }
  // Fallback: return a simple shuffled array (should not happen with current counts)
  const fallback = arr.slice();
  for (let i = fallback.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [fallback[i], fallback[j]] = [fallback[j], fallback[i]];
  }
  return fallback;
};

const SidebetWheel = () => {
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);

  const [radius, setRadius] = useState(0);
  const wheelRef = useRef(null);
  const spinTimeoutRef = useRef(null);

  const [segments, setSegments] = useState(() => arrangeSegmentsNoAdjacency(SEGMENTS));

  const gradientBackground = useMemo(() => buildGradientStops(segments), [segments]);

  const message = useMemo(() => {
    if (isSpinning) return 'Wheel is spinning — eyes on the prize!';
    if (!activeSegment) return 'Hit the button to spin for the side bet showdown!';
    return getMessageForValue(activeSegment.value);
  }, [activeSegment, isSpinning]);

  const handleSpin = () => {
    if (isSpinning) return;

    const nextSegments = arrangeSegmentsNoAdjacency(SEGMENTS);
    setSegments(nextSegments);

    const index = chooseRandomIndex(nextSegments.length);
    const selectedSegment = nextSegments[index];
    const baseRotation = getBaseRotationForIndex(index);

    window.clearTimeout(spinTimeoutRef.current);
    setActiveSegment(null);
    setIsSpinning(true);
    setRotation((prev) => {
      const normalizedPrev = ((prev % 360) + 360) % 360;
      const normalizedBase = ((baseRotation % 360) + 360) % 360;
      let deltaToBase = normalizedBase - normalizedPrev;
      if (deltaToBase < 0) {
        deltaToBase += 360;
      }
      return prev + EXTRA_ROTATIONS * 360 + deltaToBase;
    });

    spinTimeoutRef.current = window.setTimeout(() => {
      setIsSpinning(false);
      setActiveSegment(selectedSegment);
    }, SPIN_DURATION);
  };

  useEffect(() => () => {
    window.clearTimeout(spinTimeoutRef.current);
  }, []);

  useLayoutEffect(() => {
    if (!wheelRef.current) return;
    const el = wheelRef.current;
    const update = () => {
      // Prefer offsetWidth/Height (layout), fallback to getBoundingClientRect
      const w = el.offsetWidth || el.getBoundingClientRect().width;
      const h = el.offsetHeight || el.getBoundingClientRect().height;
      setRadius(Math.floor(Math.min(w, h) / 2));
    };
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, []);

  const wheelStyle = useMemo(
    () => ({
      background: `conic-gradient(from -90deg, ${gradientBackground})`,
      transform: `rotate(${rotation}deg)`,
      transition: isSpinning ? `transform ${SPIN_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)` : 'none',
      position: 'relative',
    }),
    [isSpinning, rotation, gradientBackground]
  );

  return (
    <div>

      <div className="sidebet-wheel-wrapper" style={{ position: 'relative' }}>
        <div className="sidebet-wheel" style={wheelStyle} ref={wheelRef}>
          {radius > 0 && segments.map((segment, index) => {
            const LABEL_PHASE = 3;            // angular nudge to center in wedge (deg)
            const RADIAL_INSET = 44;          // pixels from rim inward
            const TANGENTIAL_TWEAK = -4;      // pixels along the arc (negative = left, positive = right)
            const labelAngle = -90 + index * SEGMENT_ANGLE + SEGMENT_ANGLE / 2 + LABEL_PHASE;
            return (
              <div
                key={`${segment.label}-${index}`}
                className="sidebet-wheel-label"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) rotate(${labelAngle}deg) translate(${TANGENTIAL_TWEAK}px, -${Math.max(radius - RADIAL_INSET, 0)}px) rotate(90deg)`,
                  transformOrigin: 'center center',
                  textAlign: 'center',
                  pointerEvents: 'none',
                  zIndex: 10,
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.65))',
                }}
              >
                <span
                  style={{
                    color: '#111',
                    fontWeight: 800,
                    fontSize: '1rem',
                    textShadow: '0 1px 2px rgba(255,255,255,0.85), 0 -1px 2px rgba(255,255,255,0.85), 0 0 1px rgba(255,255,255,0.9)',
                  }}
                >
                  {segment.label}
                </span>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className={`sidebet-spin-button${isSpinning ? ' spinning' : ''}`}
          onClick={handleSpin}
          disabled={isSpinning}
        >
          {isSpinning ? 'Spinning…' : 'Spin'}
        </button>
        <div
          className="sidebet-wheel-pointer"
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%) rotate(180deg)',
            zIndex: 20,
            pointerEvents: 'none',
          }}
        />
      </div>
      <p
        className={`sidebet-wheel-message ${
          activeSegment?.value < 0 ? 'negative' : activeSegment?.value > 0 ? 'positive' : ''
        }`}
        style={{ textAlign: 'center' }}
      >
        {message}
      </p>
    </div>
  );
};

export default SidebetWheel;
